import React, { useMemo, useState, useEffect, useCallback, useRef } from "react"
import { Link } from "react-router-dom"
import ContentHeader from "../components/ContentHeader"
import "./MainDiffusion.css"
import ContentMain from "../components/ContentMain"
import ContentWrapper from "../components/ContentWrapper"
import DiffusionTotemCard from "../components/Diffusion/DiffusionTotemCard"
import DiffusionAdCard from "../components/Diffusion/DiffusionAdCard"
import DiffusionAdCanvaCard from "../components/Diffusion/DiffusionAdCanvaCard"
import DiffusionAiTargetingAdCard from "../components/Diffusion/AiTargeting/DiffusionAiTargetingAdCard"
import DiffusionAiTargetingCard from "../components/Diffusion/AiTargeting/DiffusionAiTargetingCard"
import AiTargetingBanner from "../components/Diffusion/AiTargeting/AiTargetingBanner"
import { openActivateAiDialog, openDeactivateAiDialog } from "../components/Diffusion/AiTargeting/AiTargetingToggleDialog"
import API from "../utils/API"
import Sweetalert from "../utils/Sweetalert"
import useFeatureFlag, { FEATURE_CODES, doesTeamHaveAccessToFeature } from "../hooks/useFeatureFlag"

export default function MainDiffusion() {
  // State
  const [ads, setAds] = useState([])
  const [adPaths, setAdPaths] = useState([])
  const [adsLoaded, setAdsLoaded] = useState(false)
  const [adPathsLoaded, setAdPathsLoaded] = useState(false)
  const [currentAdPath, setCurrentAdPath] = useState(null)
  const [totems, setTotems] = useState([])
  const [totemsLoaded, setTotemsLoaded] = useState(false)
  const [totemSelected, setTotemSelected] = useState(null)
  // AI Targeting state
  const [isAiTargetingMode, setIsAiTargetingMode] = useState(false)
  const [teamHasAiFeature, setTeamHasAiFeature] = useState(false)
  const [aiTargetingAds, setAiTargetingAds] = useState([])
  const [isAiTargetingAdsLoaded, setIsAiTargetingAdsLoaded] = useState(false)
  const { isActive: isAiTargetingFeatureActive } = useFeatureFlag(FEATURE_CODES.AI_TARGETING)
  // Transition and loading UX
  const [isSwitchingMode, setIsSwitchingMode] = useState(false)
  const [contentTransitioning, setContentTransitioning] = useState(false)
  const [effectiveAiTargetingAdsLoaded, setEffectiveAiTargetingAdsLoaded] = useState(true)
  const loadingTimersRef = useRef({ show: null, hide: null })
  const transitionTimerRef = useRef(null)

  // Comportements
  const getAds = () => {
    API.GET("/ad", (response) => {
      setAds(response.data)
      setAdsLoaded(true)
    })
  }

  const getAdPaths = () => {
    API.GET("/ad/path", (response) => {
      setAdPaths(response.data)
      setAdPathsLoaded(true)
    })
  }

  const getTotems = () => {
    API.GET("/totem", (response) => {
      setTotems(response.data)
      setTotemsLoaded(true)
    })
  }

  const checkTeamAiFeature = useCallback(() => {
    // When the team doesn't have the feature, the AI diffusion mode is not shown.
    API.GET("/user/team", (response) => {
      const team = response.data

      setTeamHasAiFeature(doesTeamHaveAccessToFeature(team, FEATURE_CODES.AI_TARGETING))
    }, () => {
      setTeamHasAiFeature(false)
    })
  }, [])

  const fetchAiTargetingAds = useCallback(() => {
    setIsAiTargetingAdsLoaded(false)

    if (totemSelected?.id && totemSelected.hasAiTargeting) {
      API.GET(`/totem-ai-targeting-ad/totem/${totemSelected.id}`, (response) => {
        setAiTargetingAds(response.data)
        setIsAiTargetingAdsLoaded(true)
      })
    } else {
      setAiTargetingAds([])
      setIsAiTargetingAdsLoaded(true)
    }
  }, [totemSelected?.id, totemSelected?.hasAiTargeting])

  const refreshTotemsAds = () => {
    setAdsLoaded(false)
    setAdPathsLoaded(false)
    setTotemsLoaded(false)
    getAds()
    getAdPaths()
    getTotems()
  }

  useMemo(() => {
    getAds()
    getAdPaths()
    getTotems()
    checkTeamAiFeature()
  }, [checkTeamAiFeature])

  useEffect(() => {
    if (isAiTargetingMode) {
      fetchAiTargetingAds()
    } else {
      setAiTargetingAds([])
    }
  }, [isAiTargetingMode, fetchAiTargetingAds])

  useEffect(() => {
    if (!totemSelected || !totemSelected.hasAiTargeting || !teamHasAiFeature || !isAiTargetingFeatureActive) {
      setIsAiTargetingMode(false)

      return
    }

    const persistedMode = totemSelected.team_totem?.isAiTargetingDiffusionModeActive ?? false

    setIsAiTargetingMode(persistedMode)
  }, [totemSelected, teamHasAiFeature, isAiTargetingFeatureActive])

  // Debounce loading spinner: don't show for very short loads, keep it visible briefly when hiding
  useEffect(() => {
    const timers = loadingTimersRef.current
    if (timers.show) clearTimeout(timers.show)
    if (timers.hide) clearTimeout(timers.hide)
    timers.show = null
    timers.hide = null

    if (!isAiTargetingMode) {
      setEffectiveAiTargetingAdsLoaded(true)
      return
    }
    if (isAiTargetingAdsLoaded) {
      timers.hide = setTimeout(() => setEffectiveAiTargetingAdsLoaded(true), 280)
      return
    }
    timers.show = setTimeout(() => setEffectiveAiTargetingAdsLoaded(false), 380)
    const showId = timers.show
    const hideId = timers.hide
    return () => {
      if (showId) clearTimeout(showId)
      if (hideId) clearTimeout(hideId)
    }
  }, [isAiTargetingMode, isAiTargetingAdsLoaded])

  // Cleanup transition timer on unmount
  useEffect(() => {
    return () => {
      if (transitionTimerRef.current) clearTimeout(transitionTimerRef.current)
    }
  }, [])

  const addAdToSelectedTotem = (ad, nVDuration, planification) => {
    let totemSelectedCopy = { ...totemSelected }
    totemSelectedCopy.ads.push({
      totem_ad: { totemId: totemSelected.id, granularity: 1, notVideoDuration: nVDuration, planification },
      ...ad,
    })
    setTotemSelected(totemSelectedCopy)
  }

  const updateAd = (adId, granularity, nVDuration, planification) => {
    let totemSelectedCopy = { ...totemSelected }
    let adIndex = totemSelectedCopy.ads.findIndex((ad) => ad.id === adId)
    totemSelectedCopy.ads[adIndex].totem_ad.granularity = granularity
    totemSelectedCopy.ads[adIndex].totem_ad.notVideoDuration = nVDuration
    totemSelectedCopy.ads[adIndex].totem_ad.planification = planification
    setTotemSelected(totemSelectedCopy)
  }

  const deleteAdFromSelectedTotem = (adId) => {
    let totemSelectedCopy = { ...totemSelected }
    let adsCopy = totemSelectedCopy.ads.filter((v) => v.id !== adId)
    totemSelectedCopy.ads = adsCopy
    setTotemSelected(totemSelectedCopy)
  }

  const validateSelectedTotem = () => {
    let totemsCopy = [...totems]
    let selectedTotemIndex = totemsCopy.findIndex((totem) => totem.id === totemSelected.id)
    totemsCopy[selectedTotemIndex] = { ...totemSelected }
    setTotems(totemsCopy)
    setTotemSelected(null)
    setIsAiTargetingMode(false)
  }

  const handleAiAdAdded = (newAd) => {
    setAiTargetingAds([...aiTargetingAds, newAd])

    fetchAiTargetingAds() // Refresh to get complete data
  }

  const applyModeTransition = (newMode) => {
    setContentTransitioning(true)

    if (transitionTimerRef.current) clearTimeout(transitionTimerRef.current)

    transitionTimerRef.current = setTimeout(() => {
      setIsAiTargetingMode(newMode)
      setCurrentAdPath(null)

      setTotemSelected((prev) => ({
        ...prev,
        team_totem: { ...prev.team_totem, isAiTargetingDiffusionModeActive: newMode },
      }))

      setTotems((prev) =>
        prev.map((t) =>
          t.id === totemSelected.id
            ? { ...t, team_totem: { ...t.team_totem, isAiTargetingDiffusionModeActive: newMode } }
            : t
        )
      )

      transitionTimerRef.current = setTimeout(() => {
        setContentTransitioning(false)
        transitionTimerRef.current = null
      }, 50)
    }, 220)
  }

  const handleToggleAiMode = async () => {
    if (contentTransitioning || isSwitchingMode) return

    const newMode = !isAiTargetingMode

    const result = newMode
      ? await openActivateAiDialog()
      : await openDeactivateAiDialog()

    if (!result.isConfirmed) return

    setIsSwitchingMode(true)

    API.PUT(
      `/totem/${totemSelected.id}/diffusion_mode`,
      { isAiTargetingDiffusionModeActive: newMode },
      () => {
        applyModeTransition(newMode)
        setIsSwitchingMode(false)
      },
      (errorMessage) => {
        Sweetalert.fireErrorToast(errorMessage || "Une erreur est survenue lors du changement de mode.")
        setIsSwitchingMode(false)
      }
    )
  }

  const canShowAiBanner = isAiTargetingFeatureActive && totemSelected && totemSelected.hasAiTargeting
  const canToggleAiMode = canShowAiBanner && teamHasAiFeature

  // Rendu
  return (
    <ContentWrapper>
      <ContentHeader>
        <div className="col-sm-6">
          <h1 className="m-0">
            Diffusion
            {canToggleAiMode && (
              <span className="ai-banner__mode-tag">
                {isAiTargetingMode ? "Diffusion intelligente" : "Mode standard"}
              </span>
            )}
          </h1>
        </div>
        <div className="col-sm-6">
          <ol className="breadcrumb float-sm-right">
            <li className="breadcrumb-item">
              <Link to={"/"}>Tableau de bord</Link>
            </li>
            <li className="breadcrumb-item active">Diffusion</li>
          </ol>
        </div>
      </ContentHeader>
      <ContentMain>
        {canShowAiBanner && (
          <AiTargetingBanner
            isAiTargetingMode={isAiTargetingMode}
            teamHasAccess={teamHasAiFeature}
            onToggleAiMode={handleToggleAiMode}
            isSwitchingMode={isSwitchingMode}
          />
        )}

        <div
          className={`main-diffusion-content row ${contentTransitioning ? "main-diffusion-content--transitioning" : ""}`}
        >
          <div className="col-xl-4">
            {isAiTargetingMode ? (
              <DiffusionAiTargetingAdCard
                ads={ads}
                adPaths={adPaths}
                loaded={adsLoaded && adPathsLoaded}
                currentAdPath={currentAdPath}
                setCurrentAdPath={setCurrentAdPath}
                totemSelected={totemSelected}
                aiTargetingAds={aiTargetingAds}
                onAdAdded={handleAiAdAdded}
              />
            ) : currentAdPath && currentAdPath.startsWith("canva") ? (
              <DiffusionAdCanvaCard
                setCurrentAdPath={setCurrentAdPath}
                totemSelected={totemSelected}
                addAdToSelectedTotem={addAdToSelectedTotem}
              />
            ) : (
              <DiffusionAdCard
                ads={ads}
                adPaths={adPaths}
                loaded={adsLoaded && adPathsLoaded}
                currentAdPath={currentAdPath}
                setCurrentAdPath={setCurrentAdPath}
                totemSelected={totemSelected}
                addAdToSelectedTotem={addAdToSelectedTotem}
              />
            )}
          </div>
          <div className="col-xl-8">
            {isAiTargetingMode ? (
              <DiffusionAiTargetingCard
                totemSelected={totemSelected}
                aiTargetingAds={aiTargetingAds}
                isAiTargetingAdsLoaded={effectiveAiTargetingAdsLoaded}
                onAdDeleted={fetchAiTargetingAds}
                onAdUpdated={fetchAiTargetingAds}
                onRefreshAds={fetchAiTargetingAds}
                onBack={validateSelectedTotem}
              />
            ) : (
              <DiffusionTotemCard
                totems={totems}
                loaded={totemsLoaded}
                totemSelected={totemSelected}
                selectTotem={setTotemSelected}
                refreshTotemsAds={refreshTotemsAds}
                validateSelectedTotem={validateSelectedTotem}
                updateAd={updateAd}
                deleteAdFromSelectedTotem={deleteAdFromSelectedTotem}
              />
            )}
          </div>
        </div>
      </ContentMain>
    </ContentWrapper>
  )
}
